// Context-aware chat notification: a short Web-Audio chime (no asset file
// needed) plus an optional browser notification. Per product spec the alert is
// SOFTER when the user is actively on the Connect page and LOUDER when they're
// away, so people aren't blasted while they're already looking at the chat.

let audioCtx: AudioContext | null = null;

function ctx(): AudioContext | null {
    try {
        const Ctor = window.AudioContext
            ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return null;
        audioCtx ??= new Ctor();
        return audioCtx;
    } catch {
        return null;
    }
}

/** True when the user is actively viewing the Connect tab. */
export function isOnConnectPage(): boolean {
    return document.visibilityState === "visible" && document.hasFocus();
}

/**
 * Short chime synthesised with the Web Audio API. <paramref name="loud"/> raises
 * the gain and adds a second tone for the "user is away" case; the soft variant
 * is a single quiet note.
 */
export function playChime(loud: boolean): void {
    const ac = ctx();
    if (!ac) return;
    // Autoplay policy: a context created without a gesture starts suspended.
    // Best-effort resume — after any click/keypress on the page it succeeds.
    if (ac.state === "suspended") void ac.resume();

    const now = ac.currentTime;
    const peak = loud ? 0.22 : 0.06;
    const notes = loud ? [880, 1174.7] : [784];
    notes.forEach((freq, i) => {
        const osc = ac.createOscillator();
        const gain = ac.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        const t = now + i * 0.12;
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.linearRampToValueAtTime(peak, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
        osc.connect(gain).connect(ac.destination);
        osc.start(t);
        osc.stop(t + 0.24);
    });
}

export interface ChatNotifyOptions {
    title: string;
    body: string;
    /** Also show a browser (OS) notification when permitted. */
    showOsNotification: boolean;
    /** Louder chime + (when shown) a notification that requires interaction. */
    loud: boolean;
    onClick?: () => void;
}

/** Fires the chime and, when requested + permitted, a browser notification. */
export function notifyChat(opts: ChatNotifyOptions): void {
    playChime(opts.loud);

    if (!opts.showOsNotification) return;
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    try {
        const n = new Notification(opts.title, {
            body: opts.body,
            tag: "athavita-connect-chat",
            // When the user is away, keep it up until they act on it.
            requireInteraction: opts.loud,
            silent: false
        });
        n.onclick = () => {
            window.focus();
            opts.onClick?.();
            n.close();
        };
    } catch {
        /* notification construction can throw on some platforms — ignore */
    }
}

/** Strips HTML to a short one-line snippet for a notification body. */
export function plainSnippet(html: string, max = 140): string {
    const text = new DOMParser().parseFromString(html ?? "", "text/html").body.textContent ?? "";
    const collapsed = text.replace(/\s+/g, " ").trim();
    return collapsed.length > max ? collapsed.slice(0, max - 1) + "…" : collapsed;
}
